import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, Badge, Spinner, Form, ListGroup, Row, Col } from 'react-bootstrap';
import { CheckCircle, XCircle, Calendar, ExternalLink, Settings, User, ArrowUp, ArrowDown } from 'lucide-react';
import { getDatabase, ref, get } from 'firebase/database';
import { workOrdersApi } from '../../../api/workOrdersApi';
import { WorkOrderWithBuilding, WorkOrderStatus, getComponentKeys, getDelegations } from '../../../types/workOrders';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import { useUsersData, Userprofile } from '../../../Helpers/UsersContext';
import WorkOrderDetailModal from './WorkOrderDetailModal';

type SortField = 'completedDate' | 'title';
type SortDirection = 'asc' | 'desc';

interface ArchivedWorkOrdersModalProps {
    show: boolean;
    onHide: () => void;
    /** Called after a work order is re-issued back to active, so the parent (e.g. the Förvaltning board) can refresh its active list. */
    onReissued?: () => void;
    /** Called after an archived work order is permanently deleted, so the parent can refresh its issues (the source issue may have been restored). */
    onDeleted?: () => void;
}

const currentYear = new Date().getFullYear();
const years = Array.from({ length: currentYear - 2019 }, (_, i) => currentYear - i);

/**
 * Modal displaying archived work orders with year filtering
 */
const ArchivedWorkOrdersModal: React.FC<ArchivedWorkOrdersModalProps> = ({
    show,
    onHide,
    onReissued,
    onDeleted
}) => {
    const { getTranslation } = useBuildComponentTypes();
    const { users } = useUsersData();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [workOrders, setWorkOrders] = useState<WorkOrderWithBuilding[]>([]);
    const [componentNames, setComponentNames] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedWorkOrderKey, setSelectedWorkOrderKey] = useState<string | null>(null);
    // Bumped to force a reload of the archive list (e.g. after a work order is
    // re-issued back to active and should disappear from the archive).
    const [reloadToken, setReloadToken] = useState(0);

    // Sorting state - default to completed date, newest first
    const [sortField, setSortField] = useState<SortField>('completedDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    // Filter state - checkboxes for each option
    const [showCompleted, setShowCompleted] = useState(true);
    const [showRecalled, setShowRecalled] = useState(true);
    const [showIssueReport, setShowIssueReport] = useState(true);
    const [showMaintenance, setShowMaintenance] = useState(true);
    const [showManual, setShowManual] = useState(true);

    // Helper to get user name from UID, showing "(extern)" for external users
    const getUserName = useCallback((uid: string, workOrder?: WorkOrderWithBuilding): string => {
        if (!users) return uid;
        const user = users.find((u: Userprofile) => u.key === uid);
        if (!user) return uid;

        // Check if this is an external user: a delegate on the (archived) work
        // order via the normalizer, or flagged isExternal. Archived records are
        // never rewritten by the lazy migration, so read via getDelegations
        // (which folds any legacy externalUserId/guestTokenKey scalar).
        const isDelegate = !!workOrder && uid in getDelegations(workOrder);
        const isExternal = isDelegate || (user as Userprofile & { isExternal?: boolean }).isExternal;
        return isExternal ? `${user.name} (extern)` : user.name;
    }, [users]);

    useEffect(() => {
        if (!show) return;

        let isMounted = true;

        const loadWorkOrders = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await workOrdersApi.getArchiveByYear(selectedYear);
                if (!isMounted) return;
                setWorkOrders(data);

                // Fetch component names - grouped by building to reduce N+1 queries
                const db = getDatabase();
                const componentsByBuilding = data.reduce((acc, wo) => {
                    const keys = getComponentKeys(wo);
                    if (keys.length > 0) {
                        if (!acc[wo.buildingKey]) acc[wo.buildingKey] = [];
                        acc[wo.buildingKey].push(...keys);
                    }
                    return acc;
                }, {} as Record<string, string[]>);

                if (Object.keys(componentsByBuilding).length > 0) {
                    const names: Record<string, string> = {};
                    // One read per building instead of one per component
                    await Promise.all(
                        Object.entries(componentsByBuilding).map(async ([buildingKey, componentKeys]) => {
                            try {
                                const snapshot = await get(ref(db, `buildings/${buildingKey}/structure/components`));
                                if (snapshot.exists()) {
                                    const components = snapshot.val();
                                    componentKeys.forEach(componentKey => {
                                        const component = components[componentKey];
                                        if (component) {
                                            const label = component?.label?.value;
                                            const specificType = component?.specificType?.value;
                                            const typeName = specificType ? getTranslation(specificType, component.type) : null;

                                            if (label && typeName) {
                                                names[componentKey] = `${typeName} (${label})`;
                                            } else if (label) {
                                                names[componentKey] = label;
                                            } else if (typeName) {
                                                names[componentKey] = typeName;
                                            } else {
                                                names[componentKey] = 'Komponent';
                                            }
                                        }
                                    });
                                }
                            } catch (err) {
                                console.error(`Error fetching components for building ${buildingKey}:`, err);
                            }
                        })
                    );
                    if (isMounted) setComponentNames(names);
                } else {
                    if (isMounted) setComponentNames({});
                }
            } catch (err) {
                console.error('Error loading archived work orders:', err);
                if (isMounted) setError('Kunde inte ladda arkiverade ärenden');
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadWorkOrders();

        return () => {
            isMounted = false;
        };
    }, [show, selectedYear, getTranslation, reloadToken]);

    const formatDate = (dateStr?: string): string => {
        if (!dateStr) return '-';
        const [datePart] = dateStr.split(' ');
        return new Date(datePart).toLocaleDateString('sv-SE');
    };

    // Filter and sort work orders
    const filteredAndSortedWorkOrders = useMemo(() => {
        let filtered = [...workOrders];

        // Apply status filter
        filtered = filtered.filter(wo => {
            if (wo.status === WorkOrderStatus.COMPLETED && !showCompleted) return false;
            if (wo.status === WorkOrderStatus.CANCELLED && !showRecalled) return false;
            return true;
        });

        // Apply source filter
        filtered = filtered.filter(wo => {
            const isIssueReport = !!wo.sourceIssueKey;
            const isMaintenance = !!wo.maintenanceTemplateId;
            const isManual = !isIssueReport && !isMaintenance;

            if (isIssueReport && !showIssueReport) return false;
            if (isMaintenance && !showMaintenance) return false;
            if (isManual && !showManual) return false;
            return true;
        });

        // Sort
        filtered.sort((a, b) => {
            let comparison = 0;

            if (sortField === 'completedDate') {
                const dateA = a.completedAt || a.updatedAt || '';
                const dateB = b.completedAt || b.updatedAt || '';
                comparison = dateA.localeCompare(dateB);
            } else if (sortField === 'title') {
                comparison = (a.title || '').localeCompare(b.title || '', 'sv');
            }

            return sortDirection === 'desc' ? -comparison : comparison;
        });

        return filtered;
    }, [workOrders, showCompleted, showRecalled, showIssueReport, showMaintenance, showManual, sortField, sortDirection]);

    const toggleSortDirection = () => {
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    const getStatusBadge = (status: WorkOrderStatus) => {
        if (status === WorkOrderStatus.COMPLETED) {
            return (
                <Badge bg="success" className="d-flex align-items-center gap-1">
                    <CheckCircle size={12} />
                    Slutförd
                </Badge>
            );
        }
        return (
            <Badge bg="secondary" className="d-flex align-items-center gap-1">
                <XCircle size={12} />
                Arkiverad utan åtgärd
            </Badge>
        );
    };

    const handleDetailModalClose = () => {
        setSelectedWorkOrderKey(null);
    };

    return (
        <>
        <Modal show={show && !selectedWorkOrderKey} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Arkiverade ärenden</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Year and Sort controls */}
                <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
                    <div>
                        <Form.Label className="fw-medium small mb-1">År</Form.Label>
                        <Form.Select
                            size="sm"
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                            style={{ width: 90 }}
                        >
                            {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </Form.Select>
                    </div>
                    <div>
                        <Form.Label className="fw-medium small mb-1">Sortering</Form.Label>
                        <div className="d-flex gap-1">
                            <Form.Select
                                size="sm"
                                value={sortField}
                                onChange={(e) => setSortField(e.target.value as SortField)}
                                style={{ width: 100 }}
                            >
                                <option value="completedDate">Datum</option>
                                <option value="title">Titel</option>
                            </Form.Select>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={toggleSortDirection}
                                title={sortDirection === 'desc' ? 'Nyast först' : 'Äldst först'}
                            >
                                {sortDirection === 'desc' ? <ArrowDown size={16} /> : <ArrowUp size={16} />}
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Filter checkboxes */}
                <div className="d-flex flex-wrap gap-4 mb-3 pb-3 border-bottom">
                    <div>
                        <div className="fw-medium small mb-1">Status</div>
                        <div className="d-flex gap-3">
                            <Form.Check
                                type="checkbox"
                                id="filter-completed"
                                label="Slutförda"
                                checked={showCompleted}
                                onChange={(e) => setShowCompleted(e.target.checked)}
                            />
                            <Form.Check
                                type="checkbox"
                                id="filter-recalled"
                                label="Arkiverade utan åtgärd"
                                checked={showRecalled}
                                onChange={(e) => setShowRecalled(e.target.checked)}
                            />
                        </div>
                    </div>
                    <div>
                        <div className="fw-medium small mb-1">Källa</div>
                        <div className="d-flex gap-3">
                            <Form.Check
                                type="checkbox"
                                id="filter-issue"
                                label="Felanmälan"
                                checked={showIssueReport}
                                onChange={(e) => setShowIssueReport(e.target.checked)}
                            />
                            <Form.Check
                                type="checkbox"
                                id="filter-maintenance"
                                label="Underhåll"
                                checked={showMaintenance}
                                onChange={(e) => setShowMaintenance(e.target.checked)}
                            />
                            <Form.Check
                                type="checkbox"
                                id="filter-manual"
                                label="Manuell"
                                checked={showManual}
                                onChange={(e) => setShowManual(e.target.checked)}
                            />
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2">Laddar...</span>
                    </div>
                ) : error ? (
                    <div className="text-center text-danger py-4">
                        {error}
                    </div>
                ) : filteredAndSortedWorkOrders.length === 0 ? (
                    <div className="text-center text-muted py-4">
                        {workOrders.length === 0
                            ? `Inga arkiverade ärenden för ${selectedYear}`
                            : 'Inga ärenden matchar filtren'
                        }
                    </div>
                ) : (
                    <ListGroup variant="flush">
                        {filteredAndSortedWorkOrders.map(wo => (
                            <ListGroup.Item
                                key={wo.key}
                                action
                                onClick={() => setSelectedWorkOrderKey(wo.key)}
                                className="d-flex justify-content-between align-items-start py-3"
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="flex-grow-1 me-3">
                                    <div className="fw-medium">{wo.title}</div>
                                    <div className="small text-muted">{wo.buildingName}</div>
                                    {getComponentKeys(wo)
                                        .filter(componentKey => componentNames[componentKey])
                                        .map(componentKey => (
                                            <div key={componentKey} className="small d-flex align-items-center gap-1 mt-1">
                                                <Settings size={12} className="text-muted" />
                                                <span
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open(`/building/${wo.buildingKey}/component/${componentKey}`, '_blank');
                                                    }}
                                                    className="text-primary text-decoration-none"
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {componentNames[componentKey]}
                                                    <ExternalLink size={10} className="ms-1" />
                                                </span>
                                            </div>
                                        ))}
                                    <div className="small text-muted d-flex align-items-center gap-1 mt-1">
                                        <Calendar size={12} />
                                        {wo.status === WorkOrderStatus.COMPLETED
                                            ? `Slutförd: ${formatDate(wo.completedAt)}`
                                            : `Arkiverad utan åtgärd: ${formatDate(wo.updatedAt)}`
                                        }
                                    </div>
                                    <div className="small text-muted d-flex align-items-center gap-1 mt-1">
                                        <User size={12} />
                                        {wo.status === WorkOrderStatus.COMPLETED
                                            ? `av ${getUserName(wo.completedBy || '', wo)}`
                                            : `av ${getUserName(wo.updatedBy || '', wo)}`
                                        }
                                    </div>
                                    {wo.actualCost !== undefined && wo.actualCost > 0 && (
                                        <div className="small text-muted mt-1">
                                            <strong>{wo.actualCost.toLocaleString('sv-SE')} kr</strong>
                                            {wo.actualCostInvestmentPercentage !== undefined && wo.actualCostInvestmentPercentage > 0 && (
                                                <span> ({wo.actualCostInvestmentPercentage}% inv.)</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    {getStatusBadge(wo.status)}
                                </div>
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Stäng
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Detail modal for viewing full work order */}
        {selectedWorkOrderKey && (
            <WorkOrderDetailModal
                show={true}
                onHide={handleDetailModalClose}
                workOrderKey={selectedWorkOrderKey}
                getUserName={getUserName}
                onDeleted={() => {
                    setReloadToken(t => t + 1);
                    onDeleted?.();
                }}
                onReissued={() => {
                    setReloadToken(t => t + 1);
                    // Bubble up so the Förvaltning board refreshes — the order is
                    // now active again and should reappear there.
                    onReissued?.();
                }}
            />
        )}
        </>
    );
};

export default ArchivedWorkOrdersModal;
